import Cookies from "js-cookie";
import { clientEnvModule } from "@/modules/client-env";
import { windowModule } from "@/modules/window";

export async function resolveCurrentGoogleAnalyticsClientIdFromBrowserCookie(): Promise<
  string | undefined
> {
  if (typeof document === "undefined") return undefined;

  try {
    const value = Cookies.get("_ga");
    if (!value) return undefined;

    // Typical formats:
    // - GA4: GA1.1.1234567890.1234567890 (client id is the last two segments joined by a dot)
    // - Older GA: GA1.2.1234567890.1234567890
    // We'll take the last two numeric-ish parts if present.
    const parts = value.split(".");
    if (parts.length >= 4) {
      const maybeId = parts.slice(-2).join(".");
      return maybeId || undefined;
    }
    return value || undefined;
  } catch {
    return undefined;
  }
}

export async function resolveCurrentGoogleAnalyticsSessionIdFromBrowserCookie(): Promise<
  string | undefined
> {
  if (typeof document === "undefined") return undefined;

  try {
    const allCookies = Cookies.get();

    // Find GA4 state cookie(s): `_ga_<measurementIdSuffix>`; exclude the base `_ga` client id cookie
    const measurementId = clientEnvModule.googleAnalyticsMeasurementId();
    const expectedSuffix = measurementId?.replace(/^G-/, "");
    const ga4StateCookies = Object.entries(allCookies)
      .filter(([name]) => name.startsWith("_ga_") && name !== "_ga")
      .map(([name, value]) => ({ name, value }));

    // Prefer cookie that matches our configured measurement id suffix, else first
    const target =
      ga4StateCookies.find(c => expectedSuffix && c.name === `_ga_${expectedSuffix}`) ||
      ga4StateCookies[0];
    if (!target || !target.value) return undefined;

    const value = target.value;

    // Primary: `$`-delimited format containing `s<session_id>` (e.g., "...$s1234567890$...")
    if (value.includes("$")) {
      // Some cookies begin with a GS prefix like GS2.1.; we can ignore it safely
      const tokens = value.replace(/^GS\d?\.\d?\./, "").split("$");
      const sessionToken = tokens.find(t => t.startsWith("s"));
      const sid = sessionToken?.slice(1);
      if (sid) return sid;
    }

    // Secondary: dot-delimited GS format (e.g., "GS1.1.<session_id>.<session_count>..." )
    const parts = value.split(".");
    if (parts.length >= 3 && parts[0].startsWith("GS")) {
      const sidCandidate = parts[2];
      if (/^\d+$/.test(sidCandidate)) return sidCandidate;
    }

    return undefined;
  } catch {
    return undefined;
  }
}

/**
 * We clear out the GA client id cookie.
 */
export async function clearGoogleAnalyticsClientIdCookie(): Promise<void> {
  // Remove basic path cookies
  Cookies.remove("_ga", { path: "/" });
  Cookies.remove("_ga", { path: "" });

  // Also remove any domain-specific cookies
  const hostname = windowModule.getLocationHostname();

  if (hostname) {
    Cookies.remove("_ga", { path: "/", domain: hostname });
    Cookies.remove("_ga", { path: "", domain: hostname });
  }
}

/**
 * We clear out all GA4 session cookies.
 */
export async function clearGoogleAnalyticsSessionIdCookies(): Promise<void> {
  Object.keys(Cookies.get()).forEach(cookieName => {
    if (cookieName.startsWith("_ga_")) {
      Cookies.remove(cookieName, { path: "/" });
    }
  });
}
